/**
 * What the room last did to its columns, read off the frame versions it keeps.
 *
 * THE ROOM CHANGES ITS OWN COLUMNS NOW. It used to hand a person a card to
 * accept for every column dropped or renamed, and two buttons ("work out the
 * columns", "fit phrases") nobody could be expected to understand. The
 * cartographer decides, the skeptic checks, the change takes effect — and this
 * is what the room says afterwards, with a way back.
 *
 * Worked out here from `frame_history` rather than sent by the server, because
 * the history is what live frame events update: the line is never stale.
 */
import type { BoardroomFrame } from '../../../types';

export interface ColumnChange {
  /** The version that made the change; undoing puts back the one before it. */
  frameId: number;
  /** Who made it official: "skeptic", "human", or the writer when nobody had to. */
  by: string;
  at: string | null;
  added: string[];
  removed: string[];
  renamed: [string, string][];
  /** This change was itself an undo. */
  isUndo: boolean;
  canUndo: boolean;
}

const names = (f?: BoardroomFrame | null) => (f?.specs ?? []).map(s => s.attribute);

export function columnChange(history: BoardroomFrame[] | null | undefined): ColumnChange | null {
  const official = (history ?? [])
    .filter(f => !f.proposed && !f.dismissed)
    .sort((a, b) => b.id - a.id);
  // Only versions with one before them: the history is the last 30, so the
  // oldest in it is not necessarily the first, and "added everything" would lie.
  for (let i = 0; i + 1 < official.length; i++) {
    const row = official[i];
    const prev = official[i + 1];
    const after = names(row);
    const before = names(prev);
    const renamed: [string, string][] = [];
    for (const s of row.specs ?? []) {
      for (const old of s.replaces ?? []) {
        if (old !== s.attribute && before.includes(old) && !after.includes(old)) renamed.push([old, s.attribute]);
      }
    }
    const renamedFrom = new Set(renamed.map(r => r[0]));
    const renamedTo = new Set(renamed.map(r => r[1]));
    const added = after.filter(a => !before.includes(a) && !renamedTo.has(a));
    const removed = before.filter(b => !after.includes(b) && !renamedFrom.has(b));
    if (!added.length && !removed.length && !renamed.length) continue;
    return {
      frameId: row.id,
      by: row.ratified_by || row.written_by,
      at: row.ratified_at || row.since,
      added,
      removed,
      renamed,
      isUndo: row.written_by === 'human' && row.copied_from_id !== null,
      canUndo: true,
    };
  }
  return null;
}

const list = (xs: string[]) => xs.join(', ');

/** "added plate · removed foam · renamed api access → api". */
export function changeWords(c: ColumnChange): string {
  const parts: string[] = [];
  if (c.added.length) parts.push(`added ${list(c.added)}`);
  if (c.renamed.length) parts.push(`renamed ${c.renamed.map(([a, b]) => `${a} → ${b}`).join(', ')}`);
  if (c.removed.length) parts.push(`removed ${list(c.removed)}`);
  return parts.join(' · ');
}
